import { Button, Card, Container, Row, Col } from "reactstrap";
import React, { useEffect, useMemo, useState, Fragment } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useDebounce } from "../../../Hooks/useDebounce";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPencil,
  faTrash,
  faClone,
} from "@fortawesome/free-solid-svg-icons";
import ViewForm from "./_component/ViewForm";
import Swal from "sweetalert2";
import {
  createServiceCommunicationThunk,
  deleteServiceCommunicationThunk,
  getServiceCommunicationData,
} from "../../../store/slices/CommunicationServiceSlice";
import CreateFormBuilder from "./_component/CreateFormBuilder";
import { Breadcrumbs } from "../../../AbstractElements";
import GetButtons from "../../../Utils/getButtons";

export default function CommunicationConfigurationPage() {
  const dispatch = useDispatch();
  const [openIsModel, setIsModel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFormDataBuilder, setSelectFormDataBuilder] = useState({});
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFilterSearch = useDebounce(columnFilters, 300);
  const [alignment, setAlignment] = React.useState("email");
  const [pagination, setPagination] = useState({
    pageIndex: 0, // current page (0-based)
    pageSize: 5, // rows per page
  });
  const [sorting, setSorting] = useState([]);

  const handleChange = (event, newAlignment) => {
    setAlignment(newAlignment);
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page when changing communication type
    setSearchQuery(""); // Clear search when changing communication type
    setSorting([]); // Reset sorting
  };

  /****View */
  const [isViewOpen, setIsViewOpen] = useState(false);

  /****handle open model */
  const handleOpenModel = () => {
    setIsModel(true);
    setSelectFormDataBuilder(data);
  };

  /***View Toggle */
  const viewToggle = () => {
    setIsViewOpen((preve) => !preve);
  };

  /***Row Action */
  const rowAction = async (type, data) => {
    /***Data Set*/
    setSelectFormDataBuilder(data);

    if (type === "DELETE") {
      Swal.fire({
        title: "Are you sure?",
        text: "This action will deactivate the form.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            // Trigger your Redux action
            console.log("Deleting form with ID:", data);
            const response = await dispatch(
              deleteServiceCommunicationThunk(data?.id),
            );

            if (response.success || !response.error) {
              setSelectFormDataBuilder({});
              Swal.fire("Deleted!", "The form has been removed.", "success");
              fetchData(debouncedSearch); // Refresh the table
            }
          } catch (error) {
            toast.error("Failed to delete the form.");
          }
        }
      });
    } else if (type === "EDIT") {
      setIsModel(true);
    } else if (type === "clone") {
      try {
        const { id, created_at, updated_at, template_key, ...rest } = data;
        const response = await dispatch(
          createServiceCommunicationThunk({
            ...rest,
            template_key: `${template_key}_clone`,
          }),
        );

        if (response.success || !response.error) {
          Swal.fire("Cloned!", "The form has been cloned.", "success");
          fetchData();
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to clone the form.", "error");
      }
    } else {
      /***Default View */
      viewToggle();
    }
  };

  const emailColumns = useMemo(
    () => [
      {
        accessorKey: "email_from",
        header: "Email From",
        size: 250,
      },
      {
        accessorKey: "email_bcc",
        header: "Email BCC",
        size: 250,
      },
      {
        accessorKey: "email_subject",
        header: "Email Subject",
        size: 250,
      },
    ],
    [alignment],
  );

  const smsColumns = useMemo(
    () => [
      {
        accessorKey: "sms_template_id",
        header: "SMS Template ID",
        size: 250,
      },
      {
        accessorKey: "sms_header",
        header: "SMS Header",
        size: 250,
      },
      {
        accessorKey: "sms_text_template",
        header: "SMS Text Template",
        size: 250,
      },
    ],
    [alignment],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "template_key", // Virtual column
        header: "Template Key",
        size: 250,
      },
      {
        accessorKey: "template_name",
        header: "Template Name",
        size: 250,
      },
      {
        accessorKey: "communication_type",
        header: "Communication Type",
        size: 200,
      },
    ],
    [alignment],
  );

  const lastColums = useMemo(
    () => [
      {
        accessorKey: "tenant_id",
        header: "Tenant ID",
        size: 250,
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 250,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        size: 250,
      },
      {
        id: "action",
        header: "Actions",
        enableSorting: false,
        size: 350,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <Button
              outline
              color="info"
              size="sm"
              onClick={() => {
                rowAction("VIEW", row?.original);
              }}
            >
              <FontAwesomeIcon data-action-type="edit" icon={faEye} />
            </Button>
            <Button
              style={{ marginRight: 0 }}
              outline
              color="primary"
              size="sm"
              onClick={() => {
                rowAction("EDIT", row?.original);
              }}
            >
              <FontAwesomeIcon data-action-type="edit" icon={faPencil} />
            </Button>
            <Button
              style={{ marginRight: 0 }}
              outline
              color="primary"
              size="sm"
              onClick={() => {
                rowAction("clone", row?.original);
              }}
            >
              <FontAwesomeIcon data-action-type="clone" icon={faClone} />
            </Button>
            <Button
              outline
              color="danger"
              size="sm"
              onClick={() => {
                rowAction("DELETE", row?.original);
              }}
            >
              <FontAwesomeIcon data-action-type="delete" icon={faTrash} />
            </Button>
          </div>
        ),
      },
    ],
    [alignment],
  );

  const [columnOrder, setColumnOrder] = useState([]);

  // ⭐ ADDED — always rebuild columnOrder so "action" stays last
  useEffect(() => {
    const getIds = (arr) =>
      arr.map((c) => c.id || c.accessorKey).filter(Boolean);

    const base = getIds(columns);
    const mid = getIds(alignment === "email" ? emailColumns : smsColumns);
    const last = getIds(lastColums);

    const all = [...base, ...mid, ...last];
    const unique = Array.from(new Set(all));
    const withoutAction = unique.filter((id) => id !== "action");

    setColumnOrder([...withoutAction, "action"]);
  }, [alignment, columns, emailColumns, smsColumns, lastColums]);

  const fetchData = async (searchQuery)  => {
    setIsLoading(true);
    console.log("Fetching data with search query:", searchQuery);
    
    const sortField = sorting.length > 0 ? sorting[0].id : undefined;
    const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

    try {
      const response = await dispatch(
        getServiceCommunicationData({
          limit: pagination.pageSize,
          page: pagination.pageIndex + 1,
          search: searchQuery,
          filters: searchQuery,
          type: alignment,
          sortBy: sortField,
          sortOrder: sortOrder,
        }),
      );
      console.log("response.payload.", response);
      if (response?.payload) {
        setData(response?.payload?.result || []);
        setTotalDataCount(response?.payload?.total);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to fetch users.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery || columnFilterSearch.length > 0) {
      console.log("Fetching with search:", searchQuery, columnFilterSearch);
      fetchData(debouncedSearch || columnFilterSearch);
    } else {
      fetchData();
    }
  }, [alignment, debouncedSearch, pagination, columnFilterSearch, sorting]);

  const handleOnChangeSearch = (value) => {
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page when search query changes
  };

  const table = useMaterialReactTable({
    columns: [
      ...columns,
      ...(alignment === "email" ? emailColumns : smsColumns),
      ...lastColums,
    ],
    data,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: totalDataCount, // Provide the total count for server-side pagination
    state: {
      pagination,
      sorting,
      columnFilters,
      globalFilter: searchQuery,
      isLoading,
      columnOrder,
      muiSkeletonProps: {
        animation: "wave",
      },
      muiLinearProgressProps: {
        color: "secondary",
      },
      muiCircularProgressProps: {
        color: "secondary",
      },
    },
    onColumnOrderChange: setColumnOrder,
    pageCount: Math.ceil(totalDataCount / pagination.pageSize),
    onGlobalFilterChange: handleOnChangeSearch,
    onColumnFiltersChange: setColumnFilters,
    initialState: { density: "compact" },
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    renderTopToolbarCustomActions: () => (
      <>
        <Box
          sx={{ display: "flex", gap: "0.3rem", p: "0.5rem", flexWrap: "wrap" }}
        >
          <GetButtons
            onClick={() => fetchData(debouncedSearch)}
            type="refresh"
          />
          <GetButtons onClick={handleOpenModel} type="add" />
          <Box>
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={alignment}
              onChange={handleChange}
              size="small"
              sx={{ height: "auto" }}
            >
              <ToggleButton value="email">Email</ToggleButton>
              <ToggleButton value="sms">SMS</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* <UncontrolledDropdown onChange={handleChange}>
            <DropdownToggle caret>Email</DropdownToggle>
            <DropdownMenu>
              <DropdownItem value="email">Email</DropdownItem>
              <DropdownItem value="sms">SMS</DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown> */}
        </Box>
      </>
    ),
  });

  return (
    <div>
      <Fragment>
        <Breadcrumbs
          mainTitle="Communication Configuration"
          parent="Configuration"
          parentPath="configuration"
          title="Configuration"
        />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <MaterialReactTable table={table} />
              </Card>
            </Col>
          </Row>
        </Container>
        {openIsModel && (
          <CreateFormBuilder
            isOpen={openIsModel}
            toggle={() => setIsModel((preve) => !preve)}
            data={selectedFormDataBuilder}
            fetchData={fetchData}
            dispatch={dispatch}
            alignment={alignment}
          />
        )}

        {/***View */}
        <ViewForm
          isOpen={isViewOpen}
          toggle={viewToggle}
          data={selectedFormDataBuilder}
          alignment={alignment}
        />
      </Fragment>
    </div>
  );
}
